/**
 * Notifications Service
 *
 * API service for notification operations.
 */

import { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface Notification {
  id: string;
  /** The backend `notificationType` (e.g. `care.intervention_touch`). */
  type: string;
  /** Coarse bucket from metadata (`care` / `system` / …). */
  category?: string;
  title: string;
  body: string;
  read: boolean;
  readAt?: string | null;
  actionUrl?: string | null;
  createdAt: string;
  /**
   * Per-type payload + optional i18n keys. Renderers prefer the stored
   * `title` / `body` — the server renders those in the recipient's locale with
   * this row's values — and fall back to `data.titleKey` / `data.bodyKey` via
   * `t()` for legacy rows and frozen mobile builds (N15). See
   * `resolveNotificationCopy` in `@tonnie/locale`.
   */
  data?: Record<string, unknown>;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class NotificationsService {
  constructor(private client: TonnieClient) {}

  /**
   * Get all notifications for the current user
   */
  async getNotifications(params?: {
    page?: number;
    pageSize?: number;
    unreadOnly?: boolean;
    /** `unread` | `read` | `dismissed` — server-side feed filter. */
    status?: 'unread' | 'read' | 'dismissed';
  }): Promise<NotificationsResponse> {
    return this.client.get<NotificationsResponse>('/api/notifications', {
      params: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    return this.client.patch<{ success: boolean }>(`/api/notifications/${notificationId}/read`);
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; count: number }> {
    return this.client.post<{ success: boolean; count: number }>('/api/notifications/read-all');
  }

  /** Dismiss (delete) one notification. */
  async deleteNotification(notificationId: string): Promise<{ id: string; dismissed: boolean }> {
    return this.client.delete<{ id: string; dismissed: boolean }>(
      `/api/notifications/${notificationId}`
    );
  }

  /** Restore a dismissed notification back into the feed (undo). */
  async restoreNotification(
    notificationId: string
  ): Promise<{ id: string; status: string; restored: boolean }> {
    return this.client.patch<{ id: string; status: string; restored: boolean }>(
      `/api/notifications/${notificationId}/restore`
    );
  }

  /** Dismiss (delete) all read notifications. */
  async deleteAllRead(): Promise<{ dismissed: number }> {
    return this.client.delete<{ dismissed: number }>('/api/notifications/read');
  }

  /** Register a push device token / Web Push subscription for the current user. */
  async registerDeviceToken(dto: {
    provider: 'expo' | 'webpush';
    token: string;
    keys?: { p256dh: string; auth: string } | null;
    platform?: string;
  }): Promise<{ registered: true }> {
    return this.client.post<{ registered: true }>('/api/notifications/device-tokens', dto);
  }

  /** Unregister a push device token / Web Push subscription (by token/endpoint). */
  async unregisterDeviceToken(token: string): Promise<{ unregistered: true }> {
    return this.client.delete<{ unregistered: true }>('/api/notifications/device-tokens', {
      token,
    });
  }
}

export function createNotificationsService(client: TonnieClient): NotificationsService {
  return new NotificationsService(client);
}
